import { Alert, Box, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useApi } from '@shared/services/api';

export interface AdminPipelineSourceResult {
  detail?: string;
  source: string;
  status: 'failed' | 'ok' | 'skipped';
}

interface PipelineJobRow {
  id: string;
  queue: string;
  state: string;
  retryCount: number;
  createdOn: string | null;
  startedOn: string | null;
  completedOn: string | null;
  data: Record<string, unknown> | null;
  output: unknown;
}

interface PlanUsageEventRow {
  id: number;
  eventType: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

interface PlanSyncLogRow {
  id: number;
  provider: string;
  status: string;
  syncType: string;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  emailsProcessed: number;
  eventsProcessed: number;
  contactsDiscovered: number;
  signalsGenerated: number;
  todosCreated: number;
}

interface PlanAdminRunRow {
  id: number;
  createdAt: string;
  actorEmail: string | null;
  metadata: Record<string, unknown> | null;
}

interface PlanWatermarks {
  contactDiscoveryStatus: string | null;
  changeCheckpoints: Array<{
    processorType: string;
    signalsProcessedAt: string | null;
    contactsProcessedAt: string | null;
  }>;
  lastCompetitorRelevanceRunAt: string | null;
  coresignalCompanyResolvedAt: string | null;
  lastTheOrgFetchAt: string | null;
  lastCoresignalEmployeeFetchAt: string | null;
  lastStakeholderMonitorAt: string | null;
}

interface PlanPipelineRuns {
  planId: number;
  jobs: PipelineJobRow[] | null;
  usageEvents: PlanUsageEventRow[];
  syncLogs: PlanSyncLogRow[];
  adminRuns: PlanAdminRunRow[];
  watermarks: PlanWatermarks;
}

const JOB_STATE_COLORS: Record<string, 'default' | 'error' | 'info' | 'success' | 'warning'> = {
  active: 'info',
  cancelled: 'default',
  completed: 'success',
  created: 'warning',
  failed: 'error',
  retry: 'warning',
};

/** One chip per pipeline source, colored by outcome, detail behind a tooltip. */
export function PipelineSourceChips({ sources }: { sources: AdminPipelineSourceResult[] }): React.ReactElement {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {sources.map((result) => (
        <Tooltip key={result.source} title={result.detail ?? ''}>
          <Chip
            size="small"
            variant={result.status === 'ok' ? 'filled' : 'outlined'}
            color={result.status === 'ok' ? 'success' : result.status === 'failed' ? 'error' : 'default'}
            label={`${result.source}: ${result.status}`}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

function formatTime(value: string | null): string {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—';
}

function isSourceResults(value: unknown): value is AdminPipelineSourceResult[] {
  return Array.isArray(value) && value.every((entry) => entry && typeof entry === 'object' && 'source' in entry);
}

/**
 * Flatten an output/metadata object into a short `key=value` line. These are
 * worker return values and event metadata — small flat objects — so a generic
 * scalar dump stays honest without a bespoke renderer per queue.
 */
function summarizeObject(value: unknown, maxLength = 180): string {
  if (value == null) return '';
  if (typeof value !== 'object') return String(value);
  const parts = Object.entries(value as Record<string, unknown>)
    .filter(([, entry]) => entry == null || ['boolean', 'number', 'string'].includes(typeof entry))
    .map(([key, entry]) => `${key}=${String(entry)}`);
  const text = parts.length ? parts.join(' · ') : JSON.stringify(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

function JobRow({ job }: { job: PipelineJobRow }): React.ReactElement {
  const started = job.startedOn ? dayjs(job.startedOn) : null;
  const completed = job.completedOn ? dayjs(job.completedOn) : null;
  const durationSeconds = started && completed ? completed.diff(started, 'second') : null;

  const output = job.output as Record<string, unknown> | null;
  const sources = output && isSourceResults(output.sources) ? output.sources : null;

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" color={JOB_STATE_COLORS[job.state] ?? 'default'} label={job.state} />
        <Typography variant="body2" fontWeight={600}>
          {job.queue}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(job.createdOn)}
          {durationSeconds !== null && ` · took ${durationSeconds}s`}
        </Typography>
        {job.retryCount > 0 && <Chip size="small" variant="outlined" color="warning" label={`retries: ${job.retryCount}`} />}
      </Stack>
      {sources && (
        <Box sx={{ mt: 0.75 }}>
          <PipelineSourceChips sources={sources} />
        </Box>
      )}
      {!sources && job.output != null && (
        <Tooltip title={JSON.stringify(job.output)}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {summarizeObject(job.output)}
          </Typography>
        </Tooltip>
      )}
    </Paper>
  );
}

function Watermark({ label, value }: { label: string; value: string | null }): React.ReactElement {
  return (
    <Box sx={{ minWidth: 200 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{formatTime(value)}</Typography>
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }} display="block">
      {children}
    </Typography>
  );
}

export interface AdminPlanPipelineRunsProps {
  accountId: number;
  planId: number;
  /** Bump to reload — e.g. after a forced run completes. */
  refreshKey: number;
}

/**
 * Recent pipeline-run evidence for one plan. There is no per-plan run table,
 * so this renders what the pipelines actually leave behind: pg-boss job rows
 * (the only per-run outcomes, kept 30 days), the plan owner's integration
 * syncs, staff-forced runs from the audit log, watermark columns, and the
 * unbounded usage-event ledger.
 */
export function AdminPlanPipelineRuns({ accountId, planId, refreshKey }: AdminPlanPipelineRunsProps): React.ReactElement {
  const api = useApi();
  const [runs, setRuns] = useState<PlanPipelineRuns | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result: any = await api.get(`/admin/account/${accountId}/plan/${planId}/pipeline-runs`);
      if (cancelled) return;
      if (result?.error) {
        setError(result.message ?? 'Failed to load pipeline runs');
        setRuns(null);
      } else {
        setRuns(result?.data ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // `api` keeps one identity per component; listing it adds nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, planId, refreshKey, reload]);

  const watermarks = runs?.watermarks;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Pipeline runs</Typography>
        <IconButton size="small" aria-label="Reload pipeline runs" onClick={() => setReload((value) => value + 1)}>
          <Refresh sx={{ fontSize: 16 }} />
        </IconButton>
        {loading && <CircularProgress size={14} />}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {runs && (
        <Stack spacing={2}>
          <Box>
            <SectionTitle>Jobs (last 30 days)</SectionTitle>
            {runs.jobs === null ? (
              <Typography variant="caption" color="text.secondary">
                Job history is unavailable — the job queue is disabled in this environment.
              </Typography>
            ) : runs.jobs.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No jobs naming this plan. Scheduled scans only appear here when they failed for this plan.
              </Typography>
            ) : (
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {runs.jobs.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <SectionTitle>Owner integration syncs</SectionTitle>
            {runs.syncLogs.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No sync history — the plan owner has no connected integrations.
              </Typography>
            ) : (
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {runs.syncLogs.map((entry) => (
                  <Paper key={entry.id} variant="outlined" sx={{ px: 1.5, py: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={entry.status === 'completed' ? 'success' : entry.status === 'failed' ? 'error' : 'info'}
                        label={entry.status}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {entry.provider}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.syncType} · {formatTime(entry.startedAt)} → {formatTime(entry.completedAt)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {entry.emailsProcessed} emails · {entry.eventsProcessed} events · {entry.contactsDiscovered}{' '}
                      contacts · {entry.signalsGenerated} signals · {entry.todosCreated} todos
                    </Typography>
                    {entry.errorMessage && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 0.25 }}>
                        {entry.errorMessage}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Syncs are per integration, not per plan — they feed every plan the owner has.
            </Typography>
          </Box>

          <Box>
            <SectionTitle>Forced runs (staff)</SectionTitle>
            {runs.adminRuns.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                None recorded.
              </Typography>
            ) : (
              runs.adminRuns.map((entry) => (
                <Typography key={entry.id} variant="caption" display="block">
                  {formatTime(entry.createdAt)} — {entry.actorEmail ?? 'unknown staff user'}
                </Typography>
              ))
            )}
          </Box>

          {watermarks && (
            <Box>
              <SectionTitle>Watermarks</SectionTitle>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Advanced only on success — a stale watermark can mean either "failing since" or "nothing new since".
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Contact discovery:
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    watermarks.contactDiscoveryStatus === 'completed'
                      ? 'success'
                      : watermarks.contactDiscoveryStatus === 'failed'
                        ? 'error'
                        : 'default'
                  }
                  label={watermarks.contactDiscoveryStatus ?? 'not recorded'}
                />
              </Stack>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Watermark label="Competitor relevance run" value={watermarks.lastCompetitorRelevanceRunAt} />
                <Watermark label="Coresignal company resolved" value={watermarks.coresignalCompanyResolvedAt} />
                <Watermark label="TheOrg org-chart fetch" value={watermarks.lastTheOrgFetchAt} />
                <Watermark label="Coresignal employee fetch" value={watermarks.lastCoresignalEmployeeFetchAt} />
                <Watermark label="Stakeholder monitor refresh" value={watermarks.lastStakeholderMonitorAt} />
                {watermarks.changeCheckpoints.map((checkpoint) => (
                  <Watermark
                    key={`${checkpoint.processorType}-signals`}
                    label={`${checkpoint.processorType}: signals processed`}
                    value={checkpoint.signalsProcessedAt}
                  />
                ))}
                {watermarks.changeCheckpoints.map((checkpoint) => (
                  <Watermark
                    key={`${checkpoint.processorType}-contacts`}
                    label={`${checkpoint.processorType}: contacts processed`}
                    value={checkpoint.contactsProcessedAt}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <SectionTitle>Pipeline activity ledger</SectionTitle>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Provider fetches and LLM calls recorded against this plan (latest {runs.usageEvents.length}). Spend only —
              no success/failure.
            </Typography>
            {runs.usageEvents.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                None recorded.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
                {runs.usageEvents.map((event) => (
                  <Tooltip key={event.id} title={JSON.stringify(event.metadata ?? {})}>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                      {formatTime(event.createdAt)} · {event.eventType ?? '?'} · {summarizeObject(event.metadata, 140)}
                    </Typography>
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}

export default AdminPlanPipelineRuns;
